import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { TransactionLogger } from '../../utils/transactionLogger';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import {
  Add,
  PlayArrow,
  Stop,
  Edit,
  Delete,
  Visibility,
  Leaderboard
} from '@mui/icons-material';

const TOURNAMENT_TYPES = {
  STROKEPLAY_SCRATCH: 'Strokeplay (Scratch)',
  STROKEPLAY_HANDICAPPED: 'Strokeplay (Handicapped)',
  MATCH_PLAY: 'Match Play',
  STABLEFORD: 'Stableford',
  SCRAMBLE: 'Scramble'
};

export default function TournamentList() {
  const { userProfile, currentUser, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Check permissions
  const canDeleteTournaments = hasPermission(userProfile?.role, PERMISSIONS.DELETE_TOURNAMENT);
  const [tournaments, setTournaments] = useState([]);
  const [golfCourses, setGolfCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  console.log('TournamentList - currentUser:', currentUser);
  console.log('TournamentList - userProfile:', userProfile);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'STROKEPLAY_SCRATCH',
    date: '',
    golfCourseId: '',
    description: '',
    maxPlayers: 100,
    entryFee: 0,
    numberOfRounds: 1,
    currentRound: 1
  });

  const fetchTournaments = async () => {
    try {
      const tournamentsQuery = query(
        collection(db, 'tournaments'),
        where('organizationId', '==', userProfile?.organizationName)
      );
      const snapshot = await getDocs(tournamentsQuery);
      const tournamentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTournaments(tournamentsData);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGolfCourses = async () => {
    try {
      const coursesQuery = query(
        collection(db, 'golfCourses'),
        where('organizationId', '==', userProfile?.organizationName)
      );
      const snapshot = await getDocs(coursesQuery);
      const coursesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGolfCourses(coursesData);
    } catch (error) {
      console.error('Error fetching golf courses:', error);
    }
  };

  useEffect(() => {
    if (userProfile?.organizationName) {
      fetchTournaments();
      fetchGolfCourses();
    }
  }, [userProfile?.organizationName]);

  useEffect(() => {
    // Auto-open dialog if navigated from dashboard
    if (location.state?.openCreateDialog) {
      setOpenDialog(true);
      // Clear the state so it doesn't open again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);
    console.log('User profile:', userProfile);
    
    // Validate required fields
    if (!formData.name.trim()) {
      alert('Please enter a tournament name');
      return;
    }
    if (!formData.date) {
      alert('Please select a date');
      return;
    }
    if (!formData.golfCourseId) {
      alert('Please select a golf course');
      return;
    }
    if (!userProfile?.organizationName) {
      alert('User profile not loaded. Please try again.');
      return;
    }
    
    try {
      // Find the selected golf course
      const selectedCourse = golfCourses.find(course => course.id === formData.golfCourseId);
      
      const tournamentData = {
        ...formData,
        organizationId: userProfile?.organizationName,
        createdAt: new Date(),
        status: 'draft',
        players: [],
        entryFee: parseFloat(formData.entryFee) || 0,
        maxPlayers: parseInt(formData.maxPlayers) || 100,
        golfCourse: selectedCourse ? {
          id: selectedCourse.id,
          name: selectedCourse.name,
          location: selectedCourse.location || '',
          address: selectedCourse.address || '',
          city: selectedCourse.city || '',
          state: selectedCourse.state || '',
          country: selectedCourse.country || '',
          totalPar: selectedCourse.totalPar,
          holes: selectedCourse.holes
        } : null
      };

      console.log('Tournament data to save:', tournamentData);

      if (editingTournament) {
        console.log('Updating existing tournament:', editingTournament.id);
        
        // Log the update transaction
        await TransactionLogger.logTransaction(
          'UPDATE',
          'TOURNAMENT',
          editingTournament.id,
          editingTournament,
          tournamentData,
          currentUser.uid,
          userProfile?.email
        );
        
        await updateDoc(doc(db, 'tournaments', editingTournament.id), tournamentData);
        setTournaments(tournaments.map(t => t.id === editingTournament.id ? { ...t, ...tournamentData } : t));
        console.log('Tournament updated successfully');
      } else {
        console.log('Creating new tournament...');
        const docRef = await addDoc(collection(db, 'tournaments'), tournamentData);
        
        // Log the creation transaction
        await TransactionLogger.logTransaction(
          'CREATE',
          'TOURNAMENT',
          docRef.id,
          null,
          tournamentData,
          currentUser.uid,
          userProfile?.email
        );
        
        console.log('Tournament created with ID:', docRef.id);
        setTournaments([...tournaments, { id: docRef.id, ...tournamentData }]);
        console.log('Tournament added to local state');
      }

      handleCloseDialog();
    } catch (error) {
      console.error('Error saving tournament:', error);
      alert('Error saving tournament: ' + error.message);
    }
  };

  const handleDelete = async (tournamentId) => {
    if (window.confirm('Are you sure you want to delete this tournament?')) {
      try {
        // Get the tournament data before deleting for logging
        const tournamentToDelete = tournaments.find(t => t.id === tournamentId);
        
        // Log the deletion transaction
        await TransactionLogger.logTransaction(
          'DELETE',
          'TOURNAMENT',
          tournamentId,
          tournamentToDelete,
          null,
          currentUser.uid,
          userProfile?.email
        );
        
        await deleteDoc(doc(db, 'tournaments', tournamentId));
        setTournaments(tournaments.filter(t => t.id !== tournamentId));
      } catch (error) {
        console.error('Error deleting tournament:', error);
      }
    }
  };

  const handleStartTournament = async (tournamentId) => {
    try {
      const tournament = tournaments.find(t => t.id === tournamentId);
      const updates = {
        status: 'active',
        startedAt: new Date()
      };
      
      // Log the status change transaction
      await TransactionLogger.logTransaction(
        'STATUS_CHANGE',
        'TOURNAMENT',
        tournamentId,
        tournament,
        { ...tournament, ...updates },
        currentUser.uid,
        userProfile?.email
      );
      
      await updateDoc(doc(db, 'tournaments', tournamentId), updates);
      setTournaments(tournaments.map(t => 
        t.id === tournamentId ? { ...t, ...updates } : t
      ));
    } catch (error) {
      console.error('Error starting tournament:', error);
    }
  };

  const handleStopTournament = async (tournamentId) => {
    try {
      const tournament = tournaments.find(t => t.id === tournamentId);
      const updates = {
        status: 'completed',
        completedAt: new Date()
      };
      
      // Log the status change transaction
      await TransactionLogger.logTransaction(
        'STATUS_CHANGE',
        'TOURNAMENT',
        tournamentId,
        tournament,
        { ...tournament, ...updates },
        currentUser.uid,
        userProfile?.email
      );
      
      await updateDoc(doc(db, 'tournaments', tournamentId), updates);
      setTournaments(tournaments.map(t => 
        t.id === tournamentId ? { ...t, ...updates } : t
      ));
    } catch (error) {
      console.error('Error stopping tournament:', error);
    }
  };

  const handleEdit = (tournament) => {
    setEditingTournament(tournament);
    setFormData({
      name: tournament.name,
      type: tournament.type,
      date: tournament.date,
      golfCourseId: tournament.golfCourse?.id || '',
      description: tournament.description,
      maxPlayers: tournament.maxPlayers,
      entryFee: tournament.entryFee,
      numberOfRounds: tournament.numberOfRounds || 1,
      currentRound: tournament.currentRound || 1
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTournament(null);
    setFormData({
      name: '',
      type: 'STROKEPLAY_SCRATCH',
      date: '',
      golfCourseId: '',
      description: '',
      maxPlayers: 100,
      entryFee: 0,
      numberOfRounds: 1,
      currentRound: 1
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'default';
      case 'active': return 'success';
      case 'completed': return 'secondary';
      default: return 'default';
    }
  };

  if (authLoading || loading) {
    return <Container><Typography>Loading tournaments...</Typography></Container>;
  }

  if (!currentUser) {
    return (
      <Container>
        <Typography variant="h5" color="error">
          Please log in to access tournaments.
        </Typography>
      </Container>
    );
  }

  if (!userProfile) {
    return (
      <Container>
        <Typography variant="h5" color="error">
          User profile not loaded. Please refresh the page or log in again.
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          Current User: {currentUser?.email || 'Not available'}
        </Typography>
        <Typography variant="body1">
          Organization: {userProfile?.organizationName || 'Not available'}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Tournaments</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenDialog(true)}
        >
          Create Tournament
        </Button>
      </Box>

      <Grid container spacing={3}>
        {tournaments.map((tournament) => (
          <Grid item xs={12} md={6} lg={4} key={tournament.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" component="h2">
                    {tournament.name}
                  </Typography>
                  <Chip 
                    label={tournament.status.toUpperCase()} 
                    color={getStatusColor(tournament.status)}
                    size="small"
                  />
                </Box>
                
                <Typography color="text.secondary" gutterBottom>
                  {TOURNAMENT_TYPES[tournament.type]}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  📅 {new Date(tournament.date).toLocaleDateString()}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  📍 {tournament.golfCourse?.name || 'No golf course selected'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  👥 {tournament.players?.length || 0} / {tournament.maxPlayers} players
                </Typography>
                
                {tournament.entryFee > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    💰 ${tournament.entryFee}
                  </Typography>
                )}
              </CardContent>
              
              <CardActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => navigate(`/tournaments/${tournament.id}/director`)}
                  color="primary"
                  fullWidth
                >
                  Director Dashboard
                </Button>

                <Button
                  size="small"
                  startIcon={<Leaderboard />}
                  onClick={() => {
                    if (tournament.type === 'STABLEFORD') {
                      navigate(`/tournaments/${tournament.id}/stableford`);
                    } else if (tournament.numberOfRounds > 1) {
                      navigate(`/tournaments/${tournament.id}/multiround`);
                    } else {
                      navigate(`/tournaments/${tournament.id}/scoreboard`);
                    }
                  }}
                >
                  Scoreboard
                </Button>

                {tournament.type === 'MATCH_PLAY' && (
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => navigate(`/tournaments/${tournament.id}/matchplay`)}
                  >
                    Bracket
                  </Button>
                )}
                
                {tournament.status === 'draft' && (
                  <Button
                    size="small"
                    startIcon={<PlayArrow />}
                    onClick={() => handleStartTournament(tournament.id)}
                    color="success"
                  >
                    Start
                  </Button>
                )}
                
                {tournament.status === 'active' && (
                  <Button
                    size="small"
                    startIcon={<Stop />}
                    onClick={() => handleStopTournament(tournament.id)}
                    color="error"
                  >
                    Stop
                  </Button>
                )}
                
                <IconButton
                  size="small"
                  onClick={() => handleEdit(tournament)}
                >
                  <Edit />
                </IconButton>
                
                {canDeleteTournaments && (
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(tournament.id)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Tournament Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTournament ? 'Edit Tournament' : 'Create New Tournament'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Tournament Name"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
            
            <FormControl fullWidth margin="dense">
              <InputLabel>Tournament Type</InputLabel>
              <Select
                value={formData.type}
                label="Tournament Type"
                onChange={(e) => setFormData({...formData, type: e.target.value})}
              >
                {Object.entries(TOURNAMENT_TYPES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              margin="dense"
              label="Date"
              type="date"
              fullWidth
              variant="outlined"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              InputLabelProps={{ shrink: true }}
              required
            />
            
            <FormControl fullWidth margin="dense">
              <InputLabel>Golf Course</InputLabel>
              <Select
                value={formData.golfCourseId}
                label="Golf Course"
                onChange={(e) => setFormData({...formData, golfCourseId: e.target.value})}
                required
              >
                {golfCourses.map((course) => (
                  <MenuItem key={course.id} value={course.id}>
                    {course.name} - {course.city && course.state && course.country
                      ? `${course.city}, ${course.state}, ${course.country}`
                      : (course.location || '')} (Par {course.totalPar})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                margin="dense"
                label="Max Players"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.maxPlayers}
                onChange={(e) => setFormData({...formData, maxPlayers: e.target.value})}
                inputProps={{ min: 1, max: 200 }}
              />
              <TextField
                margin="dense"
                label="Entry Fee ($)"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.entryFee}
                onChange={(e) => setFormData({...formData, entryFee: e.target.value})}
                inputProps={{ min: 0, step: 0.01 }}
              />
              <TextField
                margin="dense"
                label="Number of Rounds"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.numberOfRounds}
                onChange={(e) => setFormData({...formData, numberOfRounds: e.target.value})}
                inputProps={{ min: 1, max: 4 }}
                helperText="1-4 rounds"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingTournament ? 'Update' : 'Create'} Tournament
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}
